import { http } from '../request'
import { fetchAllPages, type PageResponse } from '@/utils/pagination'
import {
  type ManagementEntity,
  type ManagementImportResult,
  normalizeBoolean,
  normalizePageResponse
} from './shared'

export interface StudentDto extends ManagementEntity {
  id: number
  name: string
  studentNo: string
  gender: string
  status: string
  gradeId?: number | null
  gradeName?: string | null
  gradeFullName?: string | null
  stageName?: string | null
  classId?: number | null
  className?: string | null
  subgroupId?: number | null
  subgroupName?: string | null
  hasAccount?: boolean | null
  userId?: number | null
  roleIds?: number[]
  username?: string | null
}

type StudentPayload = {
  name: string
  studentNo: string
  gender: string | undefined
  status: string | undefined
  gradeId: number | undefined
  classId: number | undefined
  subgroupId: number | undefined
  openAccount: boolean | undefined
  roleIds: number[] | undefined
  effectiveDate?: string
  syncLoginName?: boolean
}

function toStudentPayload(payload: unknown): StudentPayload {
  const student = payload as Partial<StudentDto> & Record<string, unknown>
  const roleIdsRaw = Array.isArray(student.roleIds)
    ? student.roleIds
    : String(student.roleIds ?? '')
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)

  const roleIds = roleIdsRaw
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item) && item > 0)

  const effectiveDate = typeof student.effectiveDate === 'string' ? student.effectiveDate.trim() : ''

  return {
    name: String(student.name ?? '').trim(),
    studentNo: String(student.studentNo ?? '').trim(),
    gender: student.gender != null ? String(student.gender).trim() : undefined,
    status: student.status != null ? String(student.status).trim() : undefined,
    gradeId: student.gradeId != null ? Number(student.gradeId) : undefined,
    classId: student.classId != null ? Number(student.classId) : undefined,
    subgroupId: student.subgroupId != null ? Number(student.subgroupId) : undefined,
    openAccount: normalizeBoolean(student.openAccount),
    roleIds: roleIds.length > 0 ? roleIds : undefined,
    ...(effectiveDate ? { effectiveDate } : {}),
    syncLoginName: normalizeBoolean(student.syncLoginName)
  }
}

async function listAllStudents(): Promise<StudentDto[]> {
  return fetchAllPages<StudentDto>({
    fetchFn: (params) => http.get<PageResponse<StudentDto>>('/data-management/students', params),
    normalizeFn: (raw) => normalizePageResponse<StudentDto>(raw)
  })
}

export const studentApi = {
  async list(): Promise<StudentDto[]> {
    return listAllStudents()
  },
  async create(payload: unknown): Promise<StudentDto> {
    const response = await http.post<StudentDto>('/data-management/students', toStudentPayload(payload))
    return response.data
  },
  async update(id: number, payload: unknown): Promise<StudentDto> {
    const response = await http.put<StudentDto>(`/data-management/students/${id}`, toStudentPayload(payload))
    return response.data
  },
  async remove(id: number): Promise<void> {
    await http.delete<void>(`/data-management/students/${id}`)
  },
  async batchRemove(ids: number[]): Promise<void> {
    await http.post<void>('/data-management/students/batch-delete', { ids })
  },
  async batchUpdateStatus(ids: number[], status: string): Promise<number> {
    const response = await http.post<number>('/data-management/students/batch-update-status', { ids, status })
    return response.data
  },
  async importRows(rows: unknown[]): Promise<ManagementImportResult> {
    const response = await http.post<ManagementImportResult>(
      '/data-management/students/import',
      rows.map(toStudentPayload)
    )
    return response.data
  }
}
